import { useState, useEffect } from 'react';
import { Eye, EyeOff, Lock, Mail, Loader, AlertCircle, Sparkles, Zap, Users, Clock } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthContext';
import { authService } from '../../services/PlatformService';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, login } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (currentUser) {
      const from = location.state?.from?.pathname || '/account';
      navigate(from, { replace: true });
    }
  }, [currentUser, navigate, location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    if (!formData.email || !formData.password) {
      setError('Email e senha são obrigatórios');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const response = await authService.login(formData.email, formData.password);
      if (response && response.data) {
        login(response.data.user, response.data.token);
        const from = location.state?.from?.pathname || '/account';
        navigate(from, { replace: true });
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        setError(error.response.data.error);
      } else {
        setError('Não foi possível fazer login. Por favor, verifique suas credenciais e tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    // TODO: Implementar login com Google
    console.log('Login com Google');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 relative overflow-hidden">
      {/* Background decorativo com gradientes */}
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-40 w-80 h-80 bg-gradient-to-br from-indigo-500/20 to-indigo-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 -right-40 w-80 h-80 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl"></div>
      </div>

      {/* Banner SEO com benefícios */}
      <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-green-800 via-green-600 to-green-600 text-white py-3 px-4 text-center">
        <p className="text-sm md:text-base font-medium flex items-center justify-center gap-2">
          <Sparkles className="w-4 h-4" />
          Crie agentes IA em segundos • Sem código • 100% intuitivo
          <Sparkles className="w-4 h-4" />
        </p>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center py-20 px-4">
        <div className="max-w-2xl w-full grid lg:grid-cols-1 gap-8 items-center">
          
          {/* Lado direito - Formulário de login */}
          <div>
            <div className="bg-white/80 backdrop-blur-sm shadow-2xl rounded-3xl p-8 md:p-10 border border-gray-100">
              {/* Mostrar erro */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-2xl flex items-start mb-6">
                  <AlertCircle className="w-6 h-6 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-base">{error}</span>
                </div>
              )}

              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label className="block text-base font-semibold text-gray-700 mb-2">
                    Seu e-mail
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-400">
                      <Mail size={22} />
                    </span>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full pl-12 pr-5 py-4 text-lg border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all"
                      placeholder="seu@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-700 mb-2">
                    Sua senha
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-400">
                      <Lock size={22} />
                    </span>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full pl-12 pr-12 py-4 text-lg border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600 transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  <button
                    type="submit"
                    className="w-full py-4 px-6 border border-transparent rounded-2xl shadow-lg text-white text-lg font-semibold bg-gradient-to-r from-green-800 to-green-600 hover:from-green-700 hover:to-green-700 focus:outline-none focus:ring-4 focus:ring-indigo-200 transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader className="w-6 h-6 mr-3 animate-spin" />
                        Entrando...
                      </>
                    ) : (
                      'Entrar agora'
                    )}
                  </button>

                </div>
              </form>

              <div className="mt-8 text-center">
                <p className="text-lg text-gray-600">
                  Ainda não tem uma conta?{' '}
                  <Link to="/register" className="font-semibold text-green-600 hover:text-green-700 hover:underline transition-colors">
                    Criar conta
                  </Link>
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;